import { PageTitle, SectionHeading } from 'components/typography';
import { Container, Flex, Grid, Box, Panel } from 'components/box';
import { css, Theme } from '@emotion/react';
import RichText from 'components/rich-text';
import {
  StaticContent as StaticContentType,
  StaticContentBlock,
} from 'types/interface';
import MenuLinks from 'components/menu-links';
import { Accordion, AccordionItem } from 'components/_shared/widgets/accordion';
import Loading from 'components/loading';
import content, { StaticContentSlug } from 'shared/resources/data/content';

export const loadStaticContent = (
  contentSlug: StaticContentSlug
): StaticContentType =>
  content.find(page => page.slug === contentSlug) || {
    slug: '404-not-found',
  };

const bodyStyle = (theme: Theme) => css`
  overflow: hidden;
  a {
    font-weight: bold;
    cursor: pointer;
    color: ${theme.colors.blue};
  }

  p:last-of-type {
    padding-bottom: 0.6em;
  }
  ul {
    padding-left: 0;

    list-style-type: '—';
  }
  ul > li {
    line-height: 2;
    margin-left: 1.6em;
    padding-left: 1.6em;
  }
  h3 {
    font-family: ${theme.fonts.header};
  }
  p {
    line-height: 1.8em !important;
    width: 100% !important;
    margin-bottom: 12px;
  }
  div {
    @media screen and (max-width: ${theme.breakpoints.mobile}) {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      img {
        display: block !important;
        width: 50%;
        margin: 0 auto;
      }
      @media ${theme.mediaQueries.tabletOnly} {
        img {
          max-width: 140px;
        }
      }
    }
  }
  a {
    display: inline-block;
    img {
      display: inline-block;
      margin: 0 16px 0 0;
      width: 30px;
    }
  }
`;

const ContentHeader = ({ header }: { header: StaticContentBlock }) => (
  <>
    {header.title && (
      <PageTitle my={['0', '0']} color={header.titleColor}>
        {header.title}
      </PageTitle>
    )}

    <RichText lineHeight="1.6" content={header.copy} />
  </>
);

const ContentBody = ({ body }: { body: StaticContentBlock }) => (
  <>
    {body.title && (
      <SectionHeading mt={[3]} color={body.titleColor}>
        {body.title}
      </SectionHeading>
    )}
    <Box css={bodyStyle}>
      <RichText lineHeight="1.8" content={body.copy} />
    </Box>
  </>
);

interface LayoutProps {
  header: StaticContentBlock;
  body: StaticContentBlock[][];
  loading?: boolean;
}

const StandardLayout = ({ header, body, loading }: LayoutProps) => (
  <Loading isLoading={!!loading}>
    <Grid
      gridRowGap={0}
      gridColumnGap={[3, 5]}
      gridTemplateColumns={['1fr', null]}
    >
      <Grid
        css={css`
          grid-column: -1/1;
        `}
      >
        <ContentHeader header={header} />
      </Grid>

      {body.map((section, key) => (
        <Grid key={key}>
          {section.map((block, index) => (
            <ContentBody key={block.title || index} body={block} />
          ))}
        </Grid>
      ))}
    </Grid>
  </Loading>
);

const StackedPanelsLayout = ({ header, body, loading }: LayoutProps) => (
  <Loading isLoading={!!loading}>
    <Grid>
      {header && header.title && (
        <Grid gridColumn="-1/1">
          <ContentHeader header={header} />
        </Grid>
      )}

      {body.map((section, key) => (
        <Flex key={key} justifyContent="left" flexDirection="column">
          {section.map(panel => (
            <Panel key={panel.title} my={2} backgroundColor={panel.panelColor}>
              <ContentBody body={panel} />
            </Panel>
          ))}
        </Flex>
      ))}
    </Grid>
  </Loading>
);

const AccordionLayout = ({ header, body, loading }: LayoutProps) => (
  <Loading isLoading={!!loading}>
    <Grid width="100%">
      {header && header.title && (
        <Grid>
          <ContentHeader header={header} />
        </Grid>
      )}
      <Accordion>
        <Grid>
          {body.map((section, sectionIdx) =>
            section.map((panel, panelIdx) =>
              !panel.title && panel.copy ? (
                <RichText
                  key={`${sectionIdx}.${panelIdx}`}
                  lineHeight="1.6"
                  content={panel.copy}
                />
              ) : (
                <AccordionItem key={panel.title} title={panel.title}>
                  <Box css={bodyStyle}>
                    <RichText lineHeight="1.6" content={panel.copy} />
                  </Box>
                </AccordionItem>
              )
            )
          )}
        </Grid>
      </Accordion>
    </Grid>
  </Loading>
);

interface StaticContentProps {
  contentLayout?: string;
  contentSlug: string;
  content: StaticContentType;
  loading?: boolean;
  disableMenu?: boolean;
}

const StaticContent = ({
  contentLayout = 'standard',
  contentSlug,
  content: { contentTitle, header = {}, body = [] },
  loading,
  disableMenu,
}: StaticContentProps) => (
  <Container>
    <Box
      css={theme => css`
        @media ${theme.mediaQueries.desktopUp} {
          display: flex;
        }
      `}
    >
      {!disableMenu && <MenuLinks title={contentTitle} slug={contentSlug} />}
      <Flex>
        {contentLayout === 'standard' && (
          <StandardLayout header={header} body={body} loading={loading} />
        )}
        {contentLayout === 'stacked-panels' && (
          <StackedPanelsLayout header={header} body={body} loading={loading} />
        )}
        {contentLayout === 'accordions' && (
          <AccordionLayout header={header} body={body} loading={loading} />
        )}
      </Flex>
    </Box>
  </Container>
);

export default StaticContent;
